import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { capitalCase } from 'change-case';
import { ColumnDef } from '@tanstack/react-table';
import { RxUtils, Tr, TypeUtils } from '@utils';
import { useService } from '@hooks';
import { carModelService, CreateEasyInputDto, easyInputService, FuelType, notificationService, ParkingTimeSlot } from '@services';
import {
	addEasyInputData,
	easyInputDataSelector,
	removeEasyInputData,
	resetEasyInputData,
	updateEasyInputData
} from '@store/slices/portal.slice';
import { Button, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { DataTable, FormikNumber, FormikTextfield, Logo } from '@components/common';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { PortalRoutePath } from '@models/router';

const paddingX = 4;
const paddingY = 4;

const getParkingTimeSlotText = (inp: ParkingTimeSlot): ReactNode => {
	if (inp === ParkingTimeSlot.DAY) {
		return <Tr.Portal path="vehicle-list.parking-time-slot-day" />;
	} else if (inp === ParkingTimeSlot.NIGHT) {
		return <Tr.Portal path="vehicle-list.parking-time-slot-night" />;
	} else {
		return inp;
	}
};

export const EasyInput: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { t } = useTranslation('portal');
	const [submitDisabled, setSubmitDisabled] = useState(false);
	const { data: list = {} } = useService(() => carModelService.getUniqueList());
	const data = useSelector(easyInputDataSelector);
	const onSubmit = () => {
		RxUtils.promisify(
			easyInputService.create(data.map((d) => ({ ...d, parkingDurationMins: (d.parkingDurationMins || 0) * 60 }))),
			() => {
				notificationService.sendSuccess(t('easy-input.create-success'));
				navigate(`../${PortalRoutePath.DASHBOARD}`, { state: { auto: true } });
			}
		);
	};

	useEffect(() => {
		dispatch(resetEasyInputData());
	}, []);

	useEffect(() => {
		setSubmitDisabled(!data.length || data.some((row) => Object.values(row).some((cellValue) => !TypeUtils.isDefined(cellValue))));
	}, [data]);

	const columns = useMemo<ColumnDef<Partial<CreateEasyInputDto>>[]>(
		() => [
			{
				accessorKey: 'carListBandName',
				header: () => <Tr.Portal path="easy-input.car-list-band" />,
				enableSorting: false,
				size: 0.15,
				maxSize: 0.15,
				cell: ({ row }) => (
					<FormikTextfield
						name=""
						withoutFormik
						placeholder={t('easy-input.placeholder.car-list-band')}
						value={row.original.carListBandName || ''}
						onChange={(e) => dispatch(updateEasyInputData({ index: row.index, partial: { carListBandName: e.target.value } }))}
					/>
				)
			},
			{
				id: 'brand-model',
				header: () => <Tr.Portal path="easy-input.brand-and-model" />,
				enableSorting: false,
				size: 0.2,
				maxSize: 0.2,
				cell: ({ row }) => {
					return (
						<Stack direction="row" spacing={1} sx={{ width: 1.0 }}>
							<FormikTextfield
								name=""
								withoutFormik
								select
								placeholder={t('easy-input.placeholder.brand')}
								value={row.original.brand || ''}
								onChange={(e) =>
									dispatch(
										updateEasyInputData({
											index: row.index,
											partial: { brand: e.target.value || undefined, model: undefined, fuelType: undefined }
										})
									)
								}
								SelectProps={{
									MenuProps: {
										slotProps: {
											paper: {
												sx: { maxHeight: 10 * 36 + 2 * 8 }
											}
										}
									}
								}}
							>
								{Object.keys(list).map((brand) => (
									<MenuItem key={`brand-${brand}`} value={brand}>
										{brand}
									</MenuItem>
								))}
							</FormikTextfield>

							<FormikTextfield
								name=""
								withoutFormik
								select
								placeholder={t('easy-input.placeholder.model')}
								value={row.original.model || ''}
								disabled={!row.original.brand}
								onChange={(e) =>
									dispatch(
										updateEasyInputData({
											index: row.index,
											partial: { model: e.target.value || undefined, fuelType: undefined }
										})
									)
								}
								SelectProps={{
									MenuProps: {
										slotProps: {
											paper: {
												sx: { maxHeight: 10 * 36 + 2 * 8 }
											}
										}
									}
								}}
							>
								{(row.original.brand && list[row.original.brand] ? Object.keys(list[row.original.brand]) : []).map(
									(model) => (
										<MenuItem key={`model-${model}`} value={model}>
											{model}
										</MenuItem>
									)
								)}
							</FormikTextfield>

							{/* <Autocomplete
								options={Object.keys(list)}
								getOptionLabel={(option) => option}
								fullWidth
								inputValue={row.original.brand}
								onChange={(e, val) => {
									dispatch(
										updateEasyInputData({
											index: row.index,
											partial: { brand: val || undefined, model: undefined, fuelType: undefined }
										})
									);
								}}
								onInputChange={(_e, val, reason) => {
									dispatch(
										updateEasyInputData({
											index: row.index,
											partial: {
												brand: reason === 'input' ? val || undefined : undefined,
												model: undefined,
												fuelType: undefined
											}
										})
									);
								}}
								renderInput={(params) => (
									<FormikTextfield
										{...params}
										name=""
										placeholder={t('easy-input.placeholder.brand')}
										withoutFormik
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
							<Autocomplete
								options={row.original.brand && list[row.original.brand] ? Object.keys(list[row.original.brand]) : []}
								getOptionLabel={(option) => option}
								disabled={!row.original.brand}
								fullWidth
								inputValue={row.original.model}
								onChange={(e, val) => {
									dispatch(
										updateEasyInputData({ index: row.index, partial: { model: val || undefined, fuelType: undefined } })
									);
								}}
								onInputChange={(_e, val, reason) => {
									dispatch(
										updateEasyInputData({
											index: row.index,
											partial: {
												model: reason === 'input' ? val || undefined : undefined,
												fuelType: undefined
											}
										})
									);
								}}
								renderInput={(params) => (
									<FormikTextfield
										{...params}
										name=""
										placeholder={t('easy-input.placeholder.model')}
										withoutFormik
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/> */}
						</Stack>
					);
				}
			},
			{
				accessorKey: 'quantity',
				header: () => <Tr.Portal path="easy-input.quantity" />,
				enableSorting: false,
				size: 0.075,
				maxSize: 0.075,
				cell: ({ row }) => (
					<FormikNumber
						name=""
						withoutFormik
						placeholder={t('easy-input.placeholder.quantity')}
						value={row.original.quantity || ''}
						onChange={(e) => dispatch(updateEasyInputData({ index: row.index, partial: { quantity: +e.target.value } }))}
					/>
				)
			},
			{
				accessorKey: 'fuelType',
				header: () => <Tr.Portal path="easy-input.fuel" />,
				enableSorting: false,
				size: 0.1,
				maxSize: 0.1,
				cell: ({ row }) => {
					const fuels =
						row.original.brand && row.original.model && list?.[row.original.brand]?.[row.original.model]
							? list[row.original.brand][row.original.model]
							: Object.values(FuelType);
					return (
						<FormikTextfield
							name=""
							withoutFormik
							select
							disabled={!(row.original.brand && row.original.model && list?.[row.original.brand]?.[row.original.model])}
							placeholder={t('easy-input.placeholder.fuel')}
							value={row.original.fuelType}
							onChange={(e) =>
								dispatch(updateEasyInputData({ index: row.index, partial: { fuelType: e.target.value as FuelType } }))
							}
						>
							{fuels.map((type) => (
								<MenuItem key={`fuel-type-${type}`} value={type}>
									{capitalCase(type)}
								</MenuItem>
							))}
						</FormikTextfield>
					);
				}
			},
			{
				accessorKey: 'averageKmDaily',
				header: () => <Tr.Portal path="easy-input.average-km-daily" />,
				enableSorting: false,
				size: 0.075,
				maxSize: 0.075,
				cell: ({ row }) => (
					<FormikNumber
						name=""
						withoutFormik
						placeholder={t('easy-input.placeholder.average-km-daily')}
						value={row.original.averageKmDaily || ''}
						onChange={(e) => dispatch(updateEasyInputData({ index: row.index, partial: { averageKmDaily: +e.target.value } }))}
					/>
				)
			},
			{
				accessorKey: 'siteName',
				header: () => <Tr.Portal path="easy-input.location" />,
				enableSorting: false,
				size: 0.15,
				maxSize: 0.15,
				cell: ({ row }) => (
					<FormikTextfield
						name=""
						withoutFormik
						placeholder={t('easy-input.placeholder.location')}
						value={row.original.siteName || ''}
						onChange={(e) => dispatch(updateEasyInputData({ index: row.index, partial: { siteName: e.target.value } }))}
					/>
				)
			},
			{
				accessorKey: 'parkingTimeSlot',
				header: () => <Tr.Portal path="easy-input.parking-time-slot" />,
				enableSorting: false,
				size: 0.1,
				maxSize: 0.1,
				cell: ({ row }) => (
					<FormikTextfield
						name=""
						withoutFormik
						select
						placeholder={t('easy-input.placeholder.parking-time-slot')}
						value={row.original.parkingTimeSlot || ''}
						onChange={(e) =>
							dispatch(
								updateEasyInputData({ index: row.index, partial: { parkingTimeSlot: e.target.value as ParkingTimeSlot } })
							)
						}
					>
						{Object.values(ParkingTimeSlot).map((type) => (
							<MenuItem key={`parking-time-slot-${type}`} value={type}>
								{getParkingTimeSlotText(type)}
							</MenuItem>
						))}
					</FormikTextfield>
				)
			},
			{
				accessorKey: 'parkingDurationMins',
				header: () => <Tr.Portal path="easy-input.parking-duration" />,
				enableSorting: false,
				size: 0.15,
				maxSize: 0.15,
				cell: ({ row }) => (
					<Stack direction="row" spacing={1} alignItems="center">
						<FormikNumber
							name=""
							withoutFormik
							placeholder={t('easy-input.placeholder.parking-duration')}
							value={row.original.parkingDurationMins || ''}
							onChange={(e) =>
								dispatch(updateEasyInputData({ index: row.index, partial: { parkingDurationMins: +e.target.value } }))
							}
						/>
						<IconButton
							sx={{ color: 'primary.main', width: 48, height: 48 }}
							size="small"
							onClick={() => dispatch(removeEasyInputData(row.index))}
						>
							<RemoveCircleIcon color="inherit" />
						</IconButton>
					</Stack>
				)
			}
		],
		[list]
	);

	return (
		<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingTop: paddingY, paddingX, overflow: 'hidden' }}>
			<Logo />

			<Stack direction="row" justifyContent="space-between">
				<Stack direction="column" spacing={1}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Typography variant="h3" fontWeight={800}>
							<Tr.Portal path="easy-input.title" />
						</Typography>
					</Stack>
					<Typography>
						<Tr.Portal path="easy-input.subtitle" />
					</Typography>
				</Stack>
				<IconButton
					sx={{ width: 48, height: 48, color: 'common.black' }}
					onClick={() => navigate(`../${PortalRoutePath.DASHBOARD}`)}
				>
					<CloseIcon sx={{ width: 36, height: 36 }} color="inherit" />
				</IconButton>
			</Stack>

			<Stack direction="column" justifyContent="space-between" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
				<DataTable columns={columns} data={data} comparator={(d1, d2) => d1.index === d2.index} sx={{ overflowY: 'auto' }} />

				<Stack direction="row" spacing={4} justifyContent="center" sx={{ marginY: paddingY }}>
					<Button
						data-testid="easy-input-add-row-button"
						variant="outlined"
						color="primary"
						onClick={() => dispatch(addEasyInputData())}
					>
						<AddCircleIcon sx={{ marginRight: 1 }} />
						<Tr.Portal path="easy-input.add-row" />
					</Button>
					<Button
						data-testid="easy-input-submit-button"
						variant="contained"
						color="primary"
						disabled={submitDisabled}
						onClick={onSubmit}
					>
						<Tr.Portal path="easy-input.submit" />
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};
